import { format, isToday, isYesterday } from 'date-fns';

import { localDb } from '../localDb';

const MAX_CONVERSATIONS = 50;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export async function loadConversation({ userId, conversationId }) {
	return localDb.buddyMessages
		.where('[userId+conversationId]')
		.equals([userId, conversationId])
		.sortBy('createdAt');
}

export async function loadConversations({ userId }) {
	const allMessages = await localDb.buddyMessages
		.where('userId')
		.equals(userId)
		.sortBy('createdAt');

	const byConversation = new Map();

	for (const msg of allMessages) {
		if (!byConversation.has(msg.conversationId)) {
			byConversation.set(msg.conversationId, []);
		}
		byConversation.get(msg.conversationId).push(msg);
	}

	const conversations = [];

	for (const [conversationId, messages] of byConversation) {
		const firstUserMsg = messages.find((m) => m.role === 'user');
		const lastMsg = messages[messages.length - 1];

		conversations.push({
			conversationId,
			title: firstUserMsg ? truncate(firstUserMsg.text, 40) : 'New conversation',
			preview: lastMsg ? truncate(lastMsg.text, 60) : '',
			lastMessageAt: lastMsg.createdAt,
		});
	}

	return conversations.sort((a, b) => b.lastMessageAt - a.lastMessageAt);
}

export async function saveMessage(row) {
	await localDb.buddyMessages.add(row);
	await pruneOldMessages({ userId: row.userId });
}

export async function deleteConversation({ userId, conversationId }) {
	await localDb.buddyMessages
		.where('[userId+conversationId]')
		.equals([userId, conversationId])
		.delete();
}

export async function clearAllHistory({ userId }) {
	await localDb.buddyMessages.where('userId').equals(userId).delete();
}

export async function pruneOldMessages({ userId }) {
	const cutoff = Date.now() - MAX_AGE_MS;

	const allMessages = await localDb.buddyMessages
		.where('userId')
		.equals(userId)
		.sortBy('createdAt');

	const lastActivityByConversation = new Map();

	for (const msg of allMessages) {
		const current = lastActivityByConversation.get(msg.conversationId) ?? 0;
		if (msg.createdAt > current) {
			lastActivityByConversation.set(msg.conversationId, msg.createdAt);
		}
	}

	// Delete conversations with no activity in the last 30 days
	const expiredIds = [];

	for (const [conversationId, lastActivity] of lastActivityByConversation) {
		if (lastActivity < cutoff) expiredIds.push(conversationId);
	}

	for (const conversationId of expiredIds) {
		await deleteConversation({ userId, conversationId });
	}

	// Enforce max 50 conversations — trim the oldest when over the limit
	const activeConversations = [...lastActivityByConversation.entries()]
		.filter(([id]) => !expiredIds.includes(id))
		.sort(([, a], [, b]) => a - b);

	if (activeConversations.length > MAX_CONVERSATIONS) {
		const excess = activeConversations.slice(0, activeConversations.length - MAX_CONVERSATIONS);
		for (const [conversationId] of excess) {
			await deleteConversation({ userId, conversationId });
		}
	}
}

export function groupConversationsByDate(conversations) {
	const groups = new Map();

	for (const conv of conversations) {
		const label = getDateLabel(conv.lastMessageAt);
		if (!groups.has(label)) groups.set(label, []);
		groups.get(label).push(conv);
	}

	return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
}

function getDateLabel(createdAt) {
	if (!createdAt) return 'Unknown';
	if (isToday(createdAt)) return 'Today';
	if (isYesterday(createdAt)) return 'Yesterday';
	return format(createdAt, 'd MMM yyyy');
}

function truncate(text, maxLength) {
	if (!text || text.length <= maxLength) return text;
	return `${text.slice(0, maxLength)}…`;
}
